import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SITE } from "@/data/site";
import { PricingAddons } from "./pricing/PricingAddons";
import { useProgram } from "@/lib/programs";
import { czk } from "@/lib/price";

export default function Pricing() {
  const { program } = useProgram();
  const defaultFeatures = [
    "Kompletní mytí exteriéru + disky",
    "Šetrné sušení vzduchem a mikrovláknem",
    "Chemická i mechanická dekontaminace laku",
    "Jednokrokové strojové rozleštění",
    "Důkladné vysátí a suché čištění interiéru",
    "Ošetření plastů, kůže a obrazovek",
    "Kontrola pod LED světlem",
    "Vrácení vozu v perfektním stavu",
  ];
  const features = program.id && program.items?.length ? program.items.map((i) => i.label) : defaultFeatures;

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-brand-bg py-24 lg:py-32 border-t border-brand-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">Cena</div>
            <h2 className="mt-4 font-display font-black text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              Jeden program.{" "}
              <span className="italic font-light">Jedna cena.</span>
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed max-w-md">
              Bez skrytých příplatků. Co vidíte ve formuláři, to také zaplatíte. Cena platí
              pro standardní osobní vůz; SUV a velké vozy +{SITE.suvSurchargePct} % — vidíte to
              rovnou v rezervaci.
            </p>
            <a
              href="#reservation"
              data-testid="pricing-book-button"
              className="mt-8 inline-flex items-center gap-3 bg-brand-accent text-black font-semibold px-7 py-4 hover:bg-brand-accentHover transition-all group"
            >
              Rezervovat termín
              <span className="transition-transform group-hover:translate-x-1.5">→</span>
            </a>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative"
          >
            {/* Glowing accent border */}
            <div className="absolute -inset-px bg-gradient-to-br from-brand-accent/60 via-transparent to-brand-accent/30 blur-sm opacity-50" />
            <div className="relative bg-brand-surface border border-brand-border p-8 lg:p-12">
              <div className="absolute top-0 right-0 bg-brand-accent text-black text-xs font-bold uppercase tracking-[0.2em] px-4 py-2">
                Ušetříte 2 301 Kč
              </div>
              <div className="flex items-start justify-between flex-wrap gap-6 mt-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-brand-muted">
                    Kompletní program
                  </div>
                  <div className="mt-3 font-display font-black text-2xl lg:text-3xl" data-testid="pricing-program-title">
                    {program.title}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-brand-muted line-through font-display text-2xl">
                    {czk(program.regular_price_effective)}
                  </div>
                  <div className="font-display font-black text-5xl lg:text-7xl text-brand-accent leading-none">
                    <span data-testid="pricing-program-price">{czk(program.price)}</span>
                  </div>
                  <div className="mt-1 text-brand-muted text-xs">vše v jedné ceně</div>
                </div>
              </div>

              <div className="h-px bg-brand-border my-8" />

              <ul className="grid sm:grid-cols-2 gap-3">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                    <Check size={16} className="mt-0.5 shrink-0 text-brand-accent" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 grid sm:grid-cols-3 gap-4 text-xs text-brand-muted">
                <div className="border border-brand-border p-4">
                  <div className="text-brand-accent text-[10px] uppercase tracking-[0.2em] mb-1">
                    Doba
                  </div>
                  <div className="text-white text-sm font-medium">~ 1 den</div>
                </div>
                <div className="border border-brand-border p-4">
                  <div className="text-brand-accent text-[10px] uppercase tracking-[0.2em] mb-1">
                    Pobočky
                  </div>
                  <div className="text-white text-sm font-medium">Čestlice · Butovice</div>
                </div>
                <div className="border border-brand-border p-4">
                  <div className="text-brand-accent text-[10px] uppercase tracking-[0.2em] mb-1">
                    Záruka
                  </div>
                  <div className="text-white text-sm font-medium">Spokojenost 100 %</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        <PricingAddons />
      </div>
    </section>
  );
}
